import axios from 'axios'
import clsx from 'clsx'
import {FC, useEffect, useRef, useState} from 'react'
import {API_URL, GetBrowserData} from '../../../../_metronic/helpers'
import {toPersianDigits} from '../../../../_metronic/helpers/components/datepicker/persianDateValue'
import {TwoStepChallenge} from '../../profile/core/_models'
import {readProfileError} from '../../profile/core/_requests'
import {AuthModel} from '../core/_models'

const CODE_LENGTH = 6

const clock = (seconds: number) => {
  const value = Math.max(0, Math.ceil(seconds))
  return toPersianDigits(`${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`)
}

const useSecondsUntil = (deadline: number) => {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [deadline])
  return Math.max(0, Math.ceil((deadline - now) / 1000))
}

type Props = {
  challenge: TwoStepChallenge
  // A new code was texted; the sign-in continues with the challenge it came with.
  onChallenge: (challenge: TwoStepChallenge) => void
  onVerified: (auth: AuthModel) => Promise<void>
  // The step cannot go on (expired, too many wrong codes): back to the password, with a reason.
  onRestart: (message: string) => void
}

/**
 * «ورود دومرحله‌ای»: the code texted after a correct password. The session exists only once the
 * server accepts it (MegaPayBackend api/auth/login/two-step).
 */
export const TwoStepVerification: FC<Props> = ({challenge, onChallenge, onVerified, onRestart}) => {
  const [code, setCode] = useState('')
  const [focused, setFocused] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [deadlines, setDeadlines] = useState(() => ({
    expires: Date.now() + challenge.expiresInSeconds * 1000,
    resend: Date.now() + challenge.resendInSeconds * 1000,
  }))
  const input = useRef<HTMLInputElement>(null)
  const expiresIn = useSecondsUntil(deadlines.expires)
  const resendIn = useSecondsUntil(deadlines.resend)

  useEffect(() => {
    setDeadlines({
      expires: Date.now() + challenge.expiresInSeconds * 1000,
      resend: Date.now() + challenge.resendInSeconds * 1000,
    })
    setCode('')
    window.setTimeout(() => input.current?.focus(), 80)
  }, [challenge])

  const verify = async (value: string) => {
    if (loading || value.length !== CODE_LENGTH) return
    setLoading(true)
    setError('')
    try {
      const browser = await GetBrowserData()
      const response = await axios.post<AuthModel>(`${API_URL}/auth/login/two-step`, {
        challenge: challenge.challenge,
        code: value,
        browser,
      })
      await onVerified(response.data)
    } catch (reason) {
      const failure = readProfileError(reason, 'ورود انجام نشد. دوباره تلاش کنید.')
      if (failure.code === 'TWO_STEP_EXPIRED' || failure.code === 'TWO_STEP_SPENT') {
        onRestart(failure.message)
        return
      }
      setError(failure.message)
      setCode('')
      input.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  const resend = async () => {
    if (loading || resendIn > 0) return
    setLoading(true)
    setError('')
    try {
      const response = await axios.post<TwoStepChallenge>(`${API_URL}/auth/login/two-step/resend`, {
        challenge: challenge.challenge,
      })
      onChallenge(response.data)
    } catch (reason) {
      const failure = readProfileError(
        reason,
        'کد تازه فرستاده نشد. چند لحظه دیگر دوباره تلاش کنید.'
      )
      if (failure.code === 'TWO_STEP_EXPIRED') {
        onRestart(failure.message)
        return
      }
      setError(failure.message)
      if (failure.retryAfterSeconds) {
        setDeadlines((current) => ({
          ...current,
          resend: Date.now() + failure.retryAfterSeconds! * 1000,
        }))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='otp-stage' aria-live='polite'>
      <div className='otp-stage__heading'>
        <div>
          <strong>کد مرحله دوم را وارد کنید</strong>
          <span>
            رمز عبور درست بود. کد {toPersianDigits(CODE_LENGTH)} رقمی به{' '}
            <bdi dir='ltr'>{toPersianDigits(challenge.destination || 'موبایل شما')}</bdi> پیامک شد.
          </span>
        </div>
        <span
          className='otp-stage__timer'
          aria-label={`زمان باقی‌مانده ${clock(expiresIn)}`}
          dir='ltr'
        >
          <i className='las la-clock' aria-hidden='true' /> {clock(expiresIn)}
        </span>
      </div>

      {error && (
        <div className='auth-message auth-message--danger' role='alert'>
          <i className='las la-exclamation-circle' aria-hidden='true' />
          <span>{error}</span>
        </div>
      )}

      <div className={clsx('otp-inputs', focused && 'is-focused')} dir='ltr'>
        <input
          ref={input}
          id='twoStepCode'
          className='otp-native-input'
          type='text'
          inputMode='numeric'
          pattern='[0-9]*'
          enterKeyHint='done'
          autoComplete='one-time-code'
          maxLength={CODE_LENGTH}
          value={code}
          disabled={loading}
          aria-label={`کد ${toPersianDigits(CODE_LENGTH)} رقمی مرحله دوم`}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={(event) => {
            const value = event.target.value
              .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
              .replace(/\D/g, '')
              .slice(0, CODE_LENGTH)
            setCode(value)
            if (value.length === CODE_LENGTH) verify(value)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              verify(code)
            }
          }}
        />
        <div className='otp-boxes' aria-hidden='true'>
          {Array.from({length: CODE_LENGTH}).map((_, index) => (
            <span
              key={index}
              className={clsx(
                'otp-box',
                code[index] && 'is-filled',
                focused && index === Math.min(code.length, CODE_LENGTH - 1) && 'is-active'
              )}
            >
              {code[index] || ''}
            </span>
          ))}
        </div>
      </div>

      <button
        type='button'
        className='btn btn-primary auth-submit'
        onClick={() => verify(code)}
        disabled={loading || code.length !== CODE_LENGTH}
      >
        {loading ? (
          <span>
            <span className='spinner-border spinner-border-sm align-middle ms-2' /> در حال بررسی…
          </span>
        ) : (
          <span>
            ورود به حساب <i className='las la-arrow-left' aria-hidden='true' />
          </span>
        )}
      </button>

      <div className='otp-stage__actions'>
        <button
          type='button'
          className='auth-link-button'
          onClick={() => onRestart('')}
          disabled={loading}
        >
          <i className='las la-arrow-right' aria-hidden='true' /> بازگشت
        </button>
        <button
          type='button'
          className='auth-link-button auth-link-button--accent'
          onClick={resend}
          disabled={loading || resendIn > 0}
        >
          <i className='las la-redo-alt' aria-hidden='true' />
          {resendIn > 0 ? `ارسال دوباره تا ${clock(resendIn)}` : 'ارسال دوباره کد'}
        </button>
      </div>

      <p className='auth-two-step-note'>
        <i className='las la-shield-alt' aria-hidden='true' /> این کد را به هیچ‌کس، حتی پشتیبانی،
        ندهید.
      </p>
    </div>
  )
}
